import { AchievementManager } from './AchievementManager';
import { WizardType } from '../wizards/WizardTypes';

export type CosmeticType = 'spell_skin' | 'wizard_skin' | 'victory_animation';

export interface CosmeticDefinition {
  id: string;
  name: string;
  type: CosmeticType;
  description: string;
  preview: string; // Emoji or text preview
  unlockCondition: CosmeticUnlockCondition;
}

export type CosmeticUnlockCondition =
  | { type: 'achievement'; achievementId: string }
  | { type: 'wins_with_class'; wizardType: WizardType; count: number }
  | { type: 'season_reward'; seasonNumber: number; minTier: string }
  | { type: 'dust_purchase'; price: number };

const COSMETIC_DEFINITIONS: CosmeticDefinition[] = [
  {
    id: 'golden_fireball',
    name: 'Golden Fireball',
    type: 'spell_skin',
    description: 'Changes fireball particles to a brilliant gold',
    preview: 'golden_orb',
    unlockCondition: { type: 'achievement', achievementId: 'combo_king' },
  },
  {
    id: 'frost_crown',
    name: 'Frost Crown',
    type: 'wizard_skin',
    description: 'Adds extra crystal effects to your wizard',
    preview: 'ice_crystal',
    unlockCondition: { type: 'wins_with_class', wizardType: 'frostmage', count: 20 },
  },
  {
    id: 'victory_fireworks',
    name: 'Victory Fireworks',
    type: 'victory_animation',
    description: 'Adds firework particles to your victory celebration',
    preview: 'fireworks',
    unlockCondition: { type: 'achievement', achievementId: 'hot_streak' },
  },
];

const STORAGE_KEY_UNLOCKED = 'wizard_duel_cosmetics_unlocked';
const STORAGE_KEY_EQUIPPED = 'wizard_duel_cosmetics_equipped';

export class CosmeticManager {
  private definitions: Map<string, CosmeticDefinition>;
  private unlockedIds: Set<string>;
  private equippedIds: Map<CosmeticType, string | null>;
  private achievementManager: AchievementManager;

  constructor(achievementManager: AchievementManager) {
    this.achievementManager = achievementManager;
    this.definitions = new Map();
    for (const def of COSMETIC_DEFINITIONS) {
      this.definitions.set(def.id, def);
    }
    this.unlockedIds = this.loadUnlocked();
    this.equippedIds = this.loadEquipped();

    // Check for cosmetics that should already be unlocked
    this.checkAllUnlockConditions();
  }

  private loadUnlocked(): Set<string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_UNLOCKED);
      if (raw) {
        return new Set(JSON.parse(raw));
      }
    } catch (e) {
      console.warn('Failed to load cosmetic unlocks:', e);
    }
    return new Set();
  }

  private saveUnlocked(): void {
    try {
      localStorage.setItem(STORAGE_KEY_UNLOCKED, JSON.stringify([...this.unlockedIds]));
    } catch (e) {
      console.warn('Failed to save cosmetic unlocks:', e);
    }
  }

  private loadEquipped(): Map<CosmeticType, string | null> {
    const map = new Map<CosmeticType, string | null>([
      ['spell_skin', null],
      ['wizard_skin', null],
      ['victory_animation', null],
    ]);
    try {
      const raw = localStorage.getItem(STORAGE_KEY_EQUIPPED);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, string | null>;
        for (const [type, id] of Object.entries(parsed)) {
          if (map.has(type as CosmeticType)) {
            map.set(type as CosmeticType, id);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load equipped cosmetics:', e);
    }
    return map;
  }

  private saveEquipped(): void {
    try {
      const obj: Record<string, string | null> = {};
      for (const [type, id] of this.equippedIds.entries()) {
        obj[type] = id;
      }
      localStorage.setItem(STORAGE_KEY_EQUIPPED, JSON.stringify(obj));
    } catch (e) {
      console.warn('Failed to save equipped cosmetics:', e);
    }
  }

  checkAllUnlockConditions(): string[] {
    const newlyUnlocked: string[] = [];
    for (const def of COSMETIC_DEFINITIONS) {
      if (this.unlockedIds.has(def.id)) continue;
      if (this.isConditionMet(def.unlockCondition)) {
        this.unlockedIds.add(def.id);
        newlyUnlocked.push(def.id);
      }
    }
    if (newlyUnlocked.length > 0) {
      this.saveUnlocked();
    }
    return newlyUnlocked;
  }

  private isConditionMet(condition: CosmeticUnlockCondition): boolean {
    switch (condition.type) {
      case 'achievement': {
        const progress = this.achievementManager.getProgress(condition.achievementId);
        return progress?.unlocked === true;
      }
      case 'wins_with_class': {
        const stats = this.achievementManager.getStats();
        return stats.winsPerClass[condition.wizardType] >= condition.count;
      }
      case 'season_reward': {
        // Season rewards are granted server-side and synced to localStorage
        const seasonRewardsKey = `wizard_duel_season_rewards`;
        try {
          const raw = localStorage.getItem(seasonRewardsKey);
          if (raw) {
            const rewards = JSON.parse(raw) as string[];
            return rewards.includes(`season_${condition.seasonNumber}_${condition.minTier}`);
          }
        } catch { /* ignore */ }
        return false;
      }
      case 'dust_purchase': {
        // Dust purchases are unlocked via server-validated transactions
        return false;
      }
    }
  }

  isUnlocked(id: string): boolean {
    return this.unlockedIds.has(id);
  }

  equip(id: string): boolean {
    if (!this.unlockedIds.has(id)) return false;
    const def = this.definitions.get(id);
    if (!def) return false;
    this.equippedIds.set(def.type, id);
    this.saveEquipped();
    return true;
  }

  unequip(type: CosmeticType): void {
    this.equippedIds.set(type, null);
    this.saveEquipped();
  }

  getEquipped(type: CosmeticType): string | null {
    return this.equippedIds.get(type) ?? null;
  }

  getEquippedDefinition(type: CosmeticType): CosmeticDefinition | null {
    const id = this.equippedIds.get(type);
    if (!id) return null;
    return this.definitions.get(id) ?? null;
  }

  getDefinitions(): CosmeticDefinition[] {
    return COSMETIC_DEFINITIONS;
  }

  getDefinition(id: string): CosmeticDefinition | undefined {
    return this.definitions.get(id);
  }

  getUnlockedDefinitions(): CosmeticDefinition[] {
    return COSMETIC_DEFINITIONS.filter(def => this.unlockedIds.has(def.id));
  }

  getUnlockDescription(condition: CosmeticUnlockCondition): string {
    switch (condition.type) {
      case 'achievement': {
        const defs = this.achievementManager.getDefinitions();
        const aDef = defs.find(d => d.id === condition.achievementId);
        return aDef ? `Unlock "${aDef.name}" achievement` : 'Complete achievement';
      }
      case 'wins_with_class': {
        const classNames: Record<WizardType, string> = {
          pyromancer: 'Pyromancer',
          frostmage: 'Frost Mage',
          archmage: 'Archmage',
          battlemage: 'Battlemage',
          necromancer: 'Necromancer',
          druid: 'Druid',
          chronomancer: 'Chronomancer',
        };
        return `Win ${condition.count} matches as ${classNames[condition.wizardType]}`;
      }
      case 'season_reward': {
        const tierNames: Record<string, string> = {
          gold: 'Gold',
          platinum: 'Platinum',
          diamond: 'Diamond',
          master: 'Master',
        };
        return `Reach ${tierNames[condition.minTier] || condition.minTier} rank in Season ${condition.seasonNumber}`;
      }
      case 'dust_purchase': {
        return `Purchase for ${condition.price} Arcane Dust`;
      }
    }
  }

  /** Unlock a cosmetic by ID (called after server-validated purchase) */
  unlockCosmetic(id: string): boolean {
    if (this.unlockedIds.has(id)) return false;
    const def = this.definitions.get(id);
    if (!def) return false;
    this.unlockedIds.add(id);
    this.saveUnlocked();
    return true;
  }

  hasGoldenFireball(): boolean {
    return this.getEquipped('spell_skin') === 'golden_fireball';
  }

  hasFrostCrown(): boolean {
    return this.getEquipped('wizard_skin') === 'frost_crown';
  }

  hasVictoryFireworks(): boolean {
    return this.getEquipped('victory_animation') === 'victory_fireworks';
  }
}
